// src/contexts/UserContext.tsx
import React, { createContext, ReactNode, useState, useEffect, useCallback, useMemo } from 'react';
import { ClientProfile, CoachProfile } from '../types'; // Assuming types are defined
import clientService from '../services/clientService';
import coachService, { CoachProfileUpdatePayload } from '../services/coachService'; // Import the specific payload type
import { useAuth } from '../hooks/useAuth';

// Export the interface
export interface UserProfileContextType {
  profile: ClientProfile | CoachProfile | null;
  isLoadingProfile: boolean;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<ClientProfile | CoachProfile>) => Promise<void>;
}

// UserProfileContext uses the (now exported) UserProfileContextType
export const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const UserProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<ClientProfile | CoachProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setProfile(null);
      return;
    }
    setIsLoadingProfile(true);
    try {
      if (user.role_name === 'client') {
        const clientData = await clientService.getMyProfile();
        setProfile(clientData);
      } else if (user.role_name === 'coach') {
        const coachData = await coachService.getMyProfile();
        setProfile(coachData);
      } else {
        setProfile(null); // Admins might not have a 'client' or 'coach' profile
      }
    } catch (error) {
      console.error("Failed to fetch user's specific profile:", error);
      setProfile(null);
    } finally {
      setIsLoadingProfile(false);
    }
  }, [isAuthenticated, user]);
  
  const updateProfile = useCallback(async (data: Partial<ClientProfile | CoachProfile>) => {
    if (!isAuthenticated || !user || !profile) {
        throw new Error("User or profile not available for update.");
    }
    setIsLoadingProfile(true);
    try {
        let updatedProfile;
        if (user.role_name === 'client') {
            updatedProfile = await clientService.updateMyProfile(data as Partial<ClientProfile>);
        } else if (user.role_name === 'coach') {
            updatedProfile = await coachService.updateMyProfile(data as CoachProfileUpdatePayload);
        } else {
            throw new Error("Profile updates not supported for this role via this context.");
        }
        setProfile(updatedProfile);
    } catch (error) {
        console.error("Failed to update profile:", error);
        throw error; // Re-throw for component to handle
    } finally {
        setIsLoadingProfile(false);
    }
  }, [isAuthenticated, user, profile]);


  useEffect(() => {
    if (isAuthenticated && user) {
      fetchProfile();
    } else {
      setProfile(null); // Clear profile if user logs out
    }
  }, [isAuthenticated, user, fetchProfile]); // Added fetchProfile to dependency array

  // --- FIX ---
  // The context value is wrapped in useMemo to prevent it from being recreated
  // on every render, which was the cause of the infinite loop. The functions
  // inside are already wrapped in useCallback.
  const contextValue = useMemo(() => ({
    profile,
    isLoadingProfile,
    fetchProfile,
    updateProfile
  }), [profile, isLoadingProfile, fetchProfile, updateProfile]);
  // --- END FIX ---

  return (
    <UserProfileContext.Provider value={contextValue}>
      {children}
    </UserProfileContext.Provider>
  );
};